/**
 * Map **alert-glyph** layer (REQ-UI-069; ADR-029; ADR-032 §4.2) — the Map
 * side of the Map <-> Alerts synchronized-selection edge (REQ-UI-051 /
 * ADR-024 §5). The Alert sibling of :func:`DetectionLayer` under the
 * ADR-032 / ADR-029 cascade.
 *
 * A non-visual React component (like :func:`DetectionLayer`) that:
 *
 *   1. renders the shared alerts store's *active* rows as magenta glyphs,
 *      updating the MapLibre source in place (no re-attach churn). Per
 *      REQ-UI-069 the glyph is rendered **only when the alert resolves to a
 *      valid location** — a null-position alert is list-only in the Alerts
 *      panel (the FeatureCollection builder applies the gate);
 *   2. originates selection: clicking a glyph writes
 *      ``{kind: "alert", id}`` to the bus;
 *   3. consumes selection: highlights the selected glyph's ring and pans
 *      the Map (200 ms ease) to centre the alert when it isn't already in
 *      the viewport AND has a non-null position;
 *   4. consumes the **force-recenter** signal (the 2026-06-09 two-slot
 *      redesign, Phase 2): when the Alerts panel's "Select on Map" row menu
 *      raises :func:`requestMapFocus`, eases the camera to the alert
 *      **always** — even if already in view — distinct from the pan-if-not-
 *      in-view of a glyph click (a glyph click never raises the signal).
 *      REQ-UI-096.
 *
 * # This component only READS the alerts store
 *
 * The shared alerts store is kept warm by :func:`useMapSseBridges` (mounted
 * once in :file:`Map.tsx`), which mounts :func:`useAlertsSseBridge` against
 * the Map's live ``sseMessage`` (both new ``alert`` deltas and the
 * cross-session ``alert-dismissed`` fan-out), and by the Alerts panel, which
 * mounts its own REST back-fill feed. This layer therefore does **not**
 * re-mount any feed or SSE bridge — it subscribes to the store via
 * :func:`useActiveAlerts` (which already excludes dismissed alerts) and
 * paints. Re-mounting the SSE bridge here would double-process every delta +
 * dismissal.
 *
 * Mounted by :file:`Map.tsx` alongside the detection layer. Takes the live
 * ``map`` instance prop; it needs no ``sseMessage`` because it owns no bridge.
 *
 * Display-only mode (REQ-UI-056) is unbuilt and not gated here —
 * consistent with :func:`DetectionLayer`; it will gate all selection
 * originators centrally when it lands.
 *
 * Cross-references:
 *   - REQ-UI-069 (Alert map glyph, valid-location gate) / REQ-UI-051
 *     (Map <-> Alerts) / REQ-UI-006 (live latency) / REQ-UI-002 (WebGL) /
 *     REQ-INGEST-044 (nullable position).
 *   - ADR-024 §5 (selection contract — 200 ms ease, pan-if-not-in-view);
 *     ADR-029 §6 (Alert panel + dismissal); ADR-032 §4.2 (Alert data model).
 *   - map/use-map-sse-bridges.ts (the store-warming SSE bridge owner).
 *   - map/detection-layer.tsx (the lifecycle pattern mirrored here).
 */
import { useEffect, useRef } from "react";
import type { Map as MapLibreMap, MapLayerMouseEvent } from "maplibre-gl";
import { useActiveAlerts, useAlertsStore } from "@/alerts/alerts-store";
import { useMapSelectedIdForKind, useSelectionStore } from "@/selection/selection-store";
import { useMapFocusSignal } from "@/selection/map-focus-store";
import { stableOverrideKey, type AlertColorConfig } from "@/map/marker-styling";
import {
  ALERTS_LAYER_ID,
  attachAlertLayer,
  detachAlertLayer,
  setSelectedAlertHighlight,
} from "@/map/alert-layer-impl";
import { focusZoom } from "@/map/cluster-styling";

/**
 * Camera ease used when panning to a selected alert that isn't in the
 * viewport. Per ADR-024 §5 / REQ-UI-051 ("pans the Map with a 200 ms ease
 * to centre the … coordinates"). Same value as the detection pan.
 */
export const ALERT_PAN_DURATION_MS = 200;

/** Props for :func:`AlertLayer`. */
export interface AlertLayerProps {
  /** Live MapLibre map instance (mounted after the map's ``load``). */
  map: MapLibreMap;
  /**
   * The active Profile's alert glyph colour config (``map.alert`` ``color_mode``
   * + ``single_color`` + the per-value ``type_colors`` / ``priority_colors``
   * override maps; ADR-033 §9.7; REQ-UI-088). Drives the glyph colour:
   * ``by_alert_type`` (default) / ``by_priority`` data-driven palettes (each
   * honouring its override map, else the design-system token), or a flat
   * ``single`` colour. A config / override change re-applies the glyph paint in
   * place (no teardown).
   */
  colorConfig?: AlertColorConfig;
  /**
   * The active Profile's ``map.alert.glyph`` shape (GLYPHS batch; REQ-UI-088;
   * the closed :data:`map/glyphs.GLYPH_IDS` vocabulary, default ``diamond``).
   * Sets the SDF the glyph paints; an unknown id falls back to ``circle``. A
   * shape change re-applies the glyph's ``icon-image`` in place (no teardown).
   */
  glyph?: string;
}

/**
 * Render + wire the alert-glyph layer. Returns no DOM (WebGL-only).
 */
export function AlertLayer({ map, colorConfig, glyph }: AlertLayerProps): null {
  // Read-only: the store is fed by useMapSseBridges + the Alerts panel's
  // REST feed (see module docstring). ``useActiveAlerts`` already excludes
  // dismissed alerts. We subscribe + paint.
  const alerts = useActiveAlerts();
  // Ring + camera track the MAP selection slot (the two-slot redesign,
  // 2026-06-09): an Alerts-list left-click inspects without moving the
  // ring; only a glyph click (selectOnMap, below) sets it.
  const selectedAlertId = useMapSelectedIdForKind("alert");
  // A glyph click is map-originated → set BOTH slots (ring/pan + Entity
  // Details together).
  const selectOnMap = useSelectionStore((s) => s.selectOnMap);
  // The list-only "Select on Map" force-recenter signal (the 2026-06-09
  // two-slot redesign, Phase 2). Only the Alerts-panel row menu raises it →
  // a glyph click can't force-recenter.
  const focusSignal = useMapFocusSignal();

  // Depend on the config's primitive fields (not the object identity) so the
  // attach effect re-runs — and re-applies the glyph paint — exactly when the
  // Profile colour mode/colour changes (REQ-UI-088).
  const alertColorMode = colorConfig?.mode;
  const alertSingleColor = colorConfig?.singleColor;
  // The per-value override maps (``map.alert.type_colors`` /
  // ``priority_colors``) are objects; serialize each to a stable primitive so it
  // can be an effect dep (the glyph repaint must fire when an override changes;
  // inc-items 4/5, REQ-UI-088). Read back via refs inside the effect so the deps
  // stay honest (mirrors the snapshot layer).
  const alertTypeColors = colorConfig?.typeColors;
  const alertPriorityColors = colorConfig?.priorityColors;
  const alertTypeColorsKey = stableOverrideKey(alertTypeColors);
  const alertPriorityColorsKey = stableOverrideKey(alertPriorityColors);
  const alertTypeColorsRef = useRef(alertTypeColors);
  alertTypeColorsRef.current = alertTypeColors;
  const alertPriorityColorsRef = useRef(alertPriorityColors);
  alertPriorityColorsRef.current = alertPriorityColors;

  // Lifecycle: detach the source + layers ONCE on unmount (or a rare ``map``
  // change). Decoupled from data updates so glyph arrivals / dismissals
  // don't tear down + re-create the source.
  useEffect(() => {
    return () => {
      detachAlertLayer(map);
    };
  }, [map]);

  // Data-update: idempotent attach. First call adds the source + glyph layer
  // + highlight layer (even when empty, so the click handler + ring are
  // ready); later calls ``setData`` against the live source. Keyed on the
  // colour-config primitives + ``glyph`` too (REQ-UI-088) so a Profile
  // colour/shape change re-runs the attach, whose existing-source path
  // re-applies the glyph icon + colour in place.
  useEffect(() => {
    // Override maps are read via refs (the reactive triggers are the serialized
    // ``*ColorsKey`` deps; ``ref.current`` holds the same maps this render
    // computed the keys from) so the deps array stays honest and a per-value
    // override change repaints the glyph in place (``setPaintProperty`` on the
    // existing-source path) — never a teardown.
    attachAlertLayer(
      map,
      alerts,
      {
        mode: alertColorMode,
        singleColor: alertSingleColor,
        typeColors: alertTypeColorsRef.current,
        priorityColors: alertPriorityColorsRef.current,
      },
      glyph,
    );
  }, [
    map,
    alerts,
    alertColorMode,
    alertSingleColor,
    alertTypeColorsKey,
    alertPriorityColorsKey,
    glyph,
  ]);

  // Originate selection: a glyph click writes alert-kind selection to BOTH
  // slots (map-originated → selectOnMap; rings + Entity Details together).
  useEffect(() => {
    const onClick = (event: MapLayerMouseEvent): void => {
      const id = event.features?.[0]?.properties?.alert_id;
      if (typeof id === "string" && id.length > 0) {
        selectOnMap({ kind: "alert", id });
      }
    };
    map.on("click", ALERTS_LAYER_ID, onClick);
    return () => {
      map.off("click", ALERTS_LAYER_ID, onClick);
    };
  }, [map, selectOnMap]);

  // Consume selection — highlight ring. Re-applied on ``alerts`` change too,
  // so the filter survives a source rebuild / first attach.
  useEffect(() => {
    setSelectedAlertHighlight(map, selectedAlertId);
  }, [map, selectedAlertId, alerts]);

  // Consume selection — pan. Fires on selection *change* only (not on every
  // alerts update). Coordinates are read from the store snapshot at fire
  // time. Pans only when the alert isn't already in the viewport AND has a
  // non-null position (REQ-UI-069 — a null-position alert has no map point
  // to pan to; it's list-only). A cold selection (alert not buffered) has no
  // coordinates here and is left to Entity Details. The full store (not the
  // active selector) is read so a just-dismissed selected alert still
  // resolves its last-known coordinates without a re-pan race.
  useEffect(() => {
    if (selectedAlertId === null) {
      return;
    }
    const alert = useAlertsStore.getState().byId[selectedAlertId];
    if (alert === undefined) {
      return;
    }
    if (alert.longitude_deg === null || alert.latitude_deg === null) {
      return;
    }
    const center: [number, number] = [alert.longitude_deg, alert.latitude_deg];
    if (map.getBounds().contains(center)) {
      return;
    }
    map.easeTo({ center, duration: ALERT_PAN_DURATION_MS });
  }, [map, selectedAlertId]);

  // Consume the force-recenter signal — the list "Select on Map" path
  // (REQ-UI-096; the 2026-06-09 two-slot redesign). Fires on the focus
  // ``nonce`` change (a repeat "Select on Map" recenters again) and eases to
  // the alert **regardless** of the in-viewport check, distinct from the
  // pan-if-not-in-view of a glyph click. Gated to alert-kind + the current
  // map selection. The null-position gate (REQ-UI-069) still applies — an
  // unlocated alert has nothing to recenter to (and the panel disables the
  // menu item for it). The full store (not the active selector) is read so a
  // just-dismissed selected alert still resolves its last-known coordinates.
  useEffect(() => {
    if (focusSignal.nonce === 0) {
      return; // No focus requested yet.
    }
    const target = focusSignal.target;
    if (target === null || target.kind !== "alert") {
      return; // Not an alert focus request.
    }
    if (target.id !== selectedAlertId) {
      return; // selectOnMap sets mapSelected in the same gesture; stay in sync.
    }
    const alert = useAlertsStore.getState().byId[target.id];
    if (alert === undefined) {
      return; // Cold selection — coords unknown here; Entity Details handles it.
    }
    if (alert.longitude_deg === null || alert.latitude_deg === null) {
      return; // List-only (REQ-UI-069) — nothing to recenter to.
    }
    // Zoom IN to un-cluster (REQ-UI-096): mirrors the detection layer — an
    // explicit "Select on Map" eases to ``focusZoom`` (≥ CLUSTER_MAX_ZOOM + 1)
    // so a cluster-level map zooms in to where the glyph paints individually
    // for the ring; an already-zoomed-in map keeps its zoom. The passive
    // pan-if-not-in-view effect above does NOT change zoom.
    map.easeTo({
      center: [alert.longitude_deg, alert.latitude_deg],
      zoom: focusZoom(map.getZoom()),
      duration: ALERT_PAN_DURATION_MS,
    });
    // Keyed on ``nonce`` (the changing signal); ``selectedAlertId`` + ``target``
    // are read at fire time. Only a fresh request (nonce bump) recenters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, focusSignal.nonce]);

  return null;
}
